import type { ReactNode } from "react";
import React from "react";

import cx from "classnames";
import type { TableInstance, Row } from "react-table";
import { TableRow, TableCell, TableBody, Paper, Collapse } from "@material-ui/core";
import { CSSTransition } from "react-transition-group";
import type { IResponseWithLoading } from "App/Modules/Models";
import { useTableStyles } from "./TableStyles";
import { partialLoadingCellContent } from "./TableLoadingSkeleton";

type DefaultTableBodyProps<T extends object> = {
    instance: TableInstance<T>;
    expandDetailsTemplate?: (row: Row<T>) => ReactNode;
    loadingState?: IResponseWithLoading<any>;
    rowClassName?: (row: Row<T>) => string;
};

/** Conveinience class to render a default, sortable, potentially multi-level table header */
export const DefaultTableBody = <T extends Record<string, any>>(props: DefaultTableBodyProps<T>) => {
    const { page, prepareRow, rows } = props.instance;
    const classes = useTableStyles();

    return (
        <TableBody component="div">
            {(props.instance.pageOptions ? page : rows).map((row) => {
                prepareRow(row);
                const rowProps = row.getRowProps();
                const rowExpandedProps = row.getToggleRowExpandedProps?.();

                const combinedRowProps = {
                    ...rowProps,
                    ...rowExpandedProps,
                    style: {
                        ...rowProps.style,
                        ...rowExpandedProps?.style,
                    },
                    title: undefined,
                };

                const className = cx(
                    classes.defaultTableRow,
                    rowProps.className,
                    rowExpandedProps?.className,
                    props.rowClassName?.(row),
                );

                const isExpandable = props.expandDetailsTemplate && combinedRowProps.style.cursor === "pointer";

                return (
                    <CSSTransition
                        key={row.id}
                        in={row.isExpanded}
                        classNames={classes.tableExpandedPaper}
                        timeout={2000}
                    >
                        <Paper className={classes.tableExpandedPaper} elevation={row.isExpanded ? 6 : 0}>
                            <TableRow component="div" {...combinedRowProps} className={className} hover={isExpandable}>
                                {row.cells.map((cell, cellIndex) => {
                                    return (
                                        <TableCell
                                            key={cell.getCellProps().key}
                                            component="div"
                                            className={cell.column.isSorted ? classes.tableCellSorted : undefined}
                                            {...cell.getCellProps()}
                                            align={cell.column.align}
                                        >
                                            {props.loadingState.isPartiallyLoading && (
                                                <div className={classes.skeletonDotsOverlay}>
                                                    {partialLoadingCellContent(
                                                        cell.column,
                                                        cellIndex,
                                                        props.instance.visibleColumns.length,
                                                        classes,
                                                    )}
                                                </div>
                                            )}
                                            {cell.render("Cell")}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {props.expandDetailsTemplate && (
                                <Collapse
                                    in={row.isExpanded}
                                    className={classes.tableExpandRowCollapse}
                                    mountOnEnter
                                    unmountOnExit
                                    appear
                                >
                                    {props.expandDetailsTemplate(row)}
                                </Collapse>
                            )}
                        </Paper>
                    </CSSTransition>
                );
            })}
        </TableBody>
    );
};
